import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Users, Tag, CheckCircle } from 'lucide-react';
import { API_URLS } from '../../../config/api';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../../ui/table';

const AdminResellerHostingPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    period: '/ay',
    featured: false,
    display_order: 0,
    features: [],
    url: ''
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => { fetchPackages(); }, []);

  const decodeTurkishChars = (str) => {
    if (!str || typeof str !== 'string') return str || '';
    return str
      .replace(/Ä±/g, 'ı')
      .replace(/Ä°/g, 'İ')
      .replace(/ÄŸ/g, 'ğ')
      .replace(/Ã¼/g, 'ü')
      .replace(/Ã§/g, 'ç')
      .replace(/Ã¶/g, 'ö')
      .replace(/Ã‡/g, 'Ç')
      .replace(/Ã–/g, 'Ö')
      .replace(/Ãœ/g, 'Ü')
      .replace(/Åž/g, 'Ş')
      .replace(/ÄŸ/g, 'Ğ')
      .replace(/yÄ±l/g, 'yıl');
  };

  const decodePackage = (pkg) => {
    let rawFeatures = pkg.features ? (typeof pkg.features === 'string' ? JSON.parse(pkg.features) : pkg.features) : [];
    // Convert string features to object format { name: string, included: boolean }
    const normalizedFeatures = rawFeatures.map(f => {
      if (typeof f === 'string') {
        return { name: decodeTurkishChars(f), included: true };
      }
      if (f && typeof f === 'object') {
        return { 
          name: decodeTurkishChars(f.name || f.text || ''), 
          included: f.included !== false 
        };
      }
      return { name: String(f), included: true };
    });
    
    return {
      ...pkg,
      name: decodeTurkishChars(pkg.name),
      description: decodeTurkishChars(pkg.description),
      period: decodeTurkishChars(pkg.period),
      features: normalizedFeatures,
      display_order: pkg.display_order !== undefined ? pkg.display_order : 0,
      featured: pkg.featured == 1,
    };
  };

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_URLS.RESELLER_HOSTING_PACKAGES);
      const text = await response.text();
      let data; try { data = JSON.parse(text); } catch (e) { console.error('Non-JSON:', text); data = { data: [] }; }
      if (data.success) {
        const decoded = data.data.map(decodePackage);
        setPackages(decoded.sort((a,b)=>(a.display_order||0)-(b.display_order||0)));
      } else {
        setPackages([]);
      }
    } catch (e) {
      console.error('Fetch error:', e);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const method = editingPackage ? 'PUT' : 'POST';
      const url = editingPackage ? `${API_URLS.RESELLER_HOSTING_PACKAGES}?id=${editingPackage.id}` : API_URLS.RESELLER_HOSTING_PACKAGES;
      const payload = {
        ...formData,
        featured: formData.featured ? 1 : 0,
        features: JSON.stringify(formData.features),
      };
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const text = await response.text();
      let result; try { result = JSON.parse(text); } catch (e) { console.error('Non-JSON on save:', text); throw new Error('Invalid JSON'); }
      if (result.success) {
        fetchPackages();
        setIsDialogOpen(false);
        resetForm();
      } else {
        alert('Hata: ' + result.message);
      }
    } catch (e) {
      console.error('Save error:', e);
      alert('Paket kaydedilirken hata oluştu.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu paketi silmek istediğinizden emin misiniz?')) return;
    try {
      const response = await fetch(`${API_URLS.RESELLER_HOSTING_PACKAGES}?id=${id}`, { method: 'DELETE' });
      const text = await response.text();
      let result; try { result = JSON.parse(text); } catch (e) { console.error('Non-JSON on delete:', text); throw new Error('Invalid JSON'); }
      if (result.success) {
        fetchPackages();
      } else {
        alert('Hata: ' + result.message);
      }
    } catch (e) {
      console.error('Delete error:', e);
      alert('Paket silinirken hata oluştu.');
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      description: pkg.description || '',
      price: pkg.price,
      original_price: pkg.original_price || '',
      period: pkg.period,
      featured: pkg.featured,
      display_order: pkg.display_order,
      features: (pkg.features || []).map(f => 
        typeof f === 'object' ? { ...f } : f
      ),
      url: pkg.url || ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPackage(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      original_price: '',
      period: '/ay',
      featured: false,
      display_order: 0,
      features: [],
      url: ''
    });
    setNewFeature('');
  };

  const addFeature = () => {
    if (newFeature.trim() !== '') {
      setFormData((prev) => ({ 
        ...prev, 
        features: [...prev.features, { name: newFeature.trim(), included: true }] 
      }));
      setNewFeature('');
    }
  };

  const toggleFeatureIncluded = (idx) => {
    setFormData((prev) => {
      const updatedFeatures = [...prev.features];
      if (updatedFeatures[idx]) {
        updatedFeatures[idx] = {
          ...updatedFeatures[idx],
          included: !updatedFeatures[idx].included
        };
      }
      return { ...prev, features: updatedFeatures };
    });
  };

  const removeFeature = (index) => {
    setFormData((prev) => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
  };

  if (loading) return <div className="text-center py-8">Yükleniyor...</div>;

  return (
    <div className="p-4 sm:p-6 overflow-x-hidden">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">Bayi Hosting Paketleri</h1>
        <Button onClick={() => setIsDialogOpen(true)} className="w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" /> Yeni Paket Ekle
        </Button>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-sm">Sıra</TableHead>
              <TableHead className="text-sm">Adı</TableHead>
              <TableHead className="text-sm">Fiyat</TableHead>
              <TableHead className="text-sm">Öne Çıkan</TableHead>
              <TableHead className="text-sm">Özellikler</TableHead>
              <TableHead className="text-right text-sm">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {packages.map((pkg) => (
              <TableRow key={pkg.id}>
                <TableCell className="text-sm">{pkg.display_order}</TableCell>
                <TableCell>
                  <div className="font-medium flex items-center gap-2"><Users className="w-4 h-4 text-emerald-600 flex-shrink-0" /> <span className="break-words">{pkg.name}</span></div>
                  {pkg.description && <div className="text-sm text-gray-500 break-words">{pkg.description}</div>}
                </TableCell>
                <TableCell className="text-sm">
                  {pkg.original_price && pkg.original_price !== '' && pkg.original_price !== '0' && (
                    <span className="line-through text-gray-500 mr-2 break-words">{pkg.original_price}</span>
                  )}
                  <span className="font-semibold break-words">{pkg.price} {pkg.period}</span>
                </TableCell>
                <TableCell>
                  {pkg.featured ? <Badge variant="default" className="bg-emerald-500 text-xs">Öne Çıkan</Badge> : <Badge variant="outline" className="text-xs">Normal</Badge>}
                </TableCell>
                <TableCell>
                  <ul className="list-disc list-inside text-sm text-gray-600">
                    {(pkg.features || []).slice(0, 2).map((feature, i) => {
                      // Güvenli string extraction
                      const featureName = typeof feature === 'string' 
                        ? feature 
                        : (feature?.name || feature?.text || '');
                      const isIncluded = typeof feature === 'object' ? (feature.included !== false) : true;
                      return (
                        <li key={i} className={`break-words ${isIncluded ? '' : 'text-gray-400 line-through'}`}>{featureName}</li>
                      );
                    })}
                    {(pkg.features || []).length > 2 && <li>... ({pkg.features.length - 2} daha)</li>}
                  </ul>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(pkg)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(pkg.id)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {packages.map((pkg) => (
          <div key={pkg.id} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="break-words">{pkg.name}</span>
                </div>
                {pkg.description && <div className="text-sm text-gray-500 break-words">{pkg.description}</div>}
              </div>
              <div className="ml-2 flex-shrink-0">
                {pkg.featured ? <Badge variant="default" className="bg-emerald-500 text-xs">Öne Çıkan</Badge> : <Badge variant="outline" className="text-xs">Normal</Badge>}
              </div>
            </div>
            
            <div className="mb-3">
              <div className="text-sm text-gray-600 mb-1">Fiyat:</div>
              <div>
                {pkg.original_price && pkg.original_price !== '' && pkg.original_price !== '0' && (
                  <span className="line-through text-gray-500 mr-2 break-words">{pkg.original_price}</span>
                )}
                <span className="font-semibold break-words">{pkg.price} {pkg.period}</span>
              </div>
            </div>

            <div className="mb-3">
              <div className="text-sm text-gray-600 mb-1">Özellikler:</div>
              <ul className="list-disc list-inside text-sm text-gray-600">
                {(pkg.features || []).slice(0, 2).map((feature, i) => {
                  const featureName = typeof feature === 'string' 
                    ? feature 
                    : (feature?.name || feature?.text || '');
                  const isIncluded = typeof feature === 'object' ? (feature.included !== false) : true;
                  return (
                    <li key={i} className={`break-words ${isIncluded ? '' : 'text-gray-400 line-through'}`}>{featureName}</li>
                  );
                })}
                {(pkg.features || []).length > 2 && <li>... ({pkg.features.length - 2} daha)</li>}
              </ul>
            </div>

            <div className="flex items-center justify-between pt-3 border-t">
              <span className="text-xs text-gray-500">Sıra: {pkg.display_order}</span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(pkg)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(pkg.id)}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md md:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">{editingPackage ? 'Paketi Düzenle' : 'Yeni Paket Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="grid gap-3 sm:gap-4 py-2 sm:py-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="name" className="text-sm sm:text-right">Adı</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="sm:col-span-3 w-full" required />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="description" className="text-sm sm:text-right">Açıklama</Label>
              <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="sm:col-span-3 w-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="price" className="text-sm sm:text-right">Fiyat</Label>
              <Input id="price" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className="sm:col-span-3 w-full" required />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="original_price" className="text-sm sm:text-right">Orijinal Fiyat</Label>
              <Input id="original_price" value={formData.original_price} onChange={(e) => setFormData({ ...formData, original_price: e.target.value })} className="sm:col-span-3 w-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="period" className="text-sm sm:text-right">Periyot</Label>
              <Input id="period" value={formData.period} onChange={(e) => setFormData({ ...formData, period: e.target.value })} className="sm:col-span-3 w-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="display_order" className="text-sm sm:text-right">Sıra Numarası</Label>
              <Input id="display_order" type="number" value={formData.display_order} onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })} className="sm:col-span-3 w-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="url" className="text-sm sm:text-right">URL</Label>
              <div className="sm:col-span-3 w-full">
                <Input id="url" type="text" value={formData.url || ''} onChange={(e) => setFormData({ ...formData, url: e.target.value })} placeholder="/bayi-hosting veya https://example.com/paket" className="w-full" />
                <p className="text-xs text-gray-500 mt-1 break-words">Paket detay sayfası veya satın alma sayfası URL'si</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="featured" className="text-sm sm:text-right">Öne Çıkan</Label>
              <div className="sm:col-span-3">
                <input type="checkbox" id="featured" checked={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} className="w-4 h-4 flex-shrink-0" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start gap-2 sm:gap-4">
              <Label className="text-sm sm:text-right">Özellikler</Label>
              <div className="sm:col-span-3 space-y-2 w-full">
                {formData.features.map((feature, index) => {
                  // Güvenli string extraction
                  const featureName = typeof feature === 'string' 
                    ? feature 
                    : (feature?.name || feature?.text || '');
                  const isIncluded = typeof feature === 'object' ? (feature.included !== false) : true;
                  return (
                    <div key={index} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 border rounded px-3 py-2 bg-gray-50">
                      <div className="flex items-center gap-2 sm:gap-3 flex-1 w-full sm:w-auto min-w-0">
                        <Switch 
                          checked={isIncluded}
                          onCheckedChange={() => toggleFeatureIncluded(index)}
                          className="data-[state=checked]:bg-green-600 flex-shrink-0"
                        />
                        <Input 
                          value={featureName} 
                          onChange={(e) => {
                            const newFeatures = [...formData.features];
                            if (typeof newFeatures[index] === 'object') {
                              newFeatures[index] = { ...newFeatures[index], name: e.target.value };
                            } else {
                              newFeatures[index] = { name: e.target.value, included: true };
                            }
                            setFormData({ ...formData, features: newFeatures });
                          }} 
                          className={`flex-1 min-w-0 ${isIncluded ? '' : 'text-gray-400'}`}
                        />
                        <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0 ${isIncluded ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {isIncluded ? 'Dahil' : 'Hariç'}
                        </span>
                      </div>
                      <Button variant="destructive" size="sm" onClick={() => removeFeature(index)} className="flex-shrink-0 self-end sm:self-auto">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <Input placeholder="Yeni özellik" value={newFeature} onChange={(e) => setNewFeature(e.target.value)} className="flex-1 min-w-0" />
                  <Button type="button" onClick={addFeature} size="sm" className="flex-shrink-0 whitespace-nowrap">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="w-full sm:w-auto">İptal</Button>
              <Button type="submit" className="w-full sm:w-auto"><Save className="mr-2 h-4 w-4" /> Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminResellerHostingPackages;








































